import { useCallback, useState } from "react";

/**
 * A pane dimension the agent has dragged, remembered on this machine.
 *
 * Per-browser rather than per-account on purpose. A column width is a fact
 * about the screen it is being read on -- a 34-inch monitor and a laptop want
 * different answers from the same person -- so syncing it through the server
 * would mean docking the laptop and finding the sidebar had moved. That is the
 * complaint the drag-and-drop sidebar ordering ran into from the other
 * direction: an ordering is a preference and belongs to the person, a width is
 * a measurement and belongs to the display.
 *
 * The size is always clamped on the way in, including when it is read back:
 * a value stored against a wide screen must not open a laptop with a sidebar
 * two thirds of the way across it, and a stored `NaN` from a half-written key
 * must not size a pane to nothing.
 */
export interface PaneSize {
  /** Pixels. Already within `min` and `max`. */
  size: number;
  setSize: (next: number) => void;
  /** Back to the built-in default -- double-click, or Home on the handle. */
  reset: () => void;
}

export function usePaneSize(
  /** localStorage key. Namespaced `pulse.pane.*` by every caller. */
  key: string,
  fallback: number,
  bounds: { min: number; max: number },
): PaneSize {
  const [size, setStored] = useState(() =>
    clamp(read(key) ?? fallback, bounds.min, bounds.max),
  );

  const setSize = useCallback(
    (next: number) => {
      const value = clamp(next, bounds.min, bounds.max);
      setStored(value);
      try {
        window.localStorage.setItem(key, String(Math.round(value)));
      } catch {
        /* Private modes refuse storage. A width that cannot be remembered is
           still worth honouring for the session. */
      }
    },
    [key, bounds.min, bounds.max],
  );

  const reset = useCallback(() => {
    setStored(clamp(fallback, bounds.min, bounds.max));
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* See setSize. */
    }
  }, [key, fallback, bounds.min, bounds.max]);

  return { size, setSize, reset };
}

function read(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
